import { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  PaginationState,
  ColumnDef,
} from "@tanstack/react-table";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { IOC } from "../../models/SherlockModel";
import { msalInstance } from "../../index";
import { getPagedSherlockData } from "../../service/apiAccessLogic";
import { setPagedIOCs } from "../../redux/sherlockSlice";

const columnHelper = createColumnHelper<IOC>();

const editorColumns: ColumnDef<IOC, any>[] = [
  columnHelper.accessor((row) => atob(row.id), {
    id: "indicator",
    header: "Indicator",
    cell: (info) => <div className="whitespace-nowrap">{info.getValue()}</div>,
  }),
  columnHelper.accessor((row) => row.timestamp, {
    id: "timestamp",
    header: "Timestamp",
    cell: (info) => {
      const ts = info.getValue() as string;
      return ts.replace("T", " ").slice(0, ts.indexOf("."));
    },
  }),
  columnHelper.accessor("threat_level_id", { header: "Threat Level" }),
  columnHelper.accessor("info", {
    header: "Info",
    cell: (info) => <div className="max-w-[40ch] truncate">{info.getValue()}</div>,
  }),
  columnHelper.accessor((row) => row.expiry_date, {
    id: "expiry_date",
    header: "Expiry Date",
    cell: (info) => {
      const ts = info.getValue() as string;
      return ts.replace("T", " ").slice(0, ts.indexOf("."));
    },
  }),
  columnHelper.accessor("indicator_id", { header: "Indicator ID" }),
  columnHelper.accessor("org_id", { header: "Org ID" }),
  columnHelper.accessor("orgc_id", { header: "Orgc ID" }),
  columnHelper.accessor("published", { header: "Published" }),
  columnHelper.accessor("locked", { header: "Locked" }),
];

const clientColumns: ColumnDef<IOC, any>[] = [
  columnHelper.accessor((row) => {
    const ts = row.timestamp;
    return ts.replace("T", " ").slice(0, ts.indexOf("."));
  }, { id: "timestamp", header: "Timestamp" }),
  columnHelper.accessor("threat_level_id", { header: "Threat Level" }),
  columnHelper.accessor("info", {
    header: "Info",
    cell: (info) => <div className="max-w-[40ch] truncate">{info.getValue()}</div>,
  }),
  columnHelper.accessor((row) => {
    const ts = row.expiry_date;
    return ts.replace("T", " ").slice(0, ts.indexOf("."));
  }, { id: "expiry_date", header: "Expiry Date" }),
  columnHelper.accessor("indicator_id", { header: "Indicator ID" }),
];

const PagedIOCsTable = () => {
  const userMode = useAppSelector((state) => state.user.userMode);
  const iocs = useAppSelector((state) => state.sherlock.pagedIOCs) as IOC[];
  const pageSizeSetting = useAppSelector((state) => state.sherlock.pageSize);
  const pageEnd = useAppSelector((state) => state.sherlock.pagedIOCsEnd);
  const display = useAppSelector((state) => state.sherlock.pagedIOCsDisplay);
  const dispatch = useAppDispatch();

  const [columns, setColumns] = useState<ColumnDef<IOC, any>[]>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSizeSetting,
  });

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize]
  );

  const paginatedIOCs = useMemo(
    () => iocs.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [iocs, pageIndex, pageSize]
  );

  const table = useReactTable({
    data: paginatedIOCs || [],
    columns,
    pageCount: Math.ceil((iocs.length || 0) / pageSize) || 1,
    state: { pagination },
    onPaginationChange: setPagination,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Paginated fetching (unchanged semantics)
  useEffect(() => {
    if (pageEnd) return;
    if (
      iocs.length === pageSize ||
      (pageIndex + 1) * pageSize === iocs.length
    ) {
      if (iocs.length === 0) return;
      getPagedSherlockData(
        msalInstance,
        "indicators",
        (iocs[iocs.length - 1] as any)["_ts"],
        (iocs[iocs.length - 1] as any)["uuid"]
      )
        .then((response) => dispatch(setPagedIOCs(response.data)))
        .catch((err) => {
          console.error("Error fetching iocs", err);
        });
    }
  }, [pageIndex, pageSize, iocs, dispatch, pageEnd]);

  useEffect(() => {
    setColumns(userMode === "editor" ? editorColumns : clientColumns);
  }, [userMode]);

  if (!display) return null;

  if (!iocs.length) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-8 w-full animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="text-left text-gray-600">
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="py-2 pr-4">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-t">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="py-2 pr-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {"<<"}
          </button>
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {"<"}
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {">"}
          </button>
          <button
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            className="px-2 py-1 text-sm border rounded disabled:opacity-50"
          >
            {">>"}
          </button>
        </div>

        <span className="text-sm text-gray-600">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </span>

        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="px-2 py-1 text-sm border rounded"
        >
          {[25, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default PagedIOCsTable;
